import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Brain, CheckSquare, FolderOpen, Lightbulb, Sparkles } from "lucide-react";
import { cn } from "../../lib/utils";
import { mmTypeStyle } from "../../lib/mmTypeStyle";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useSpaces } from "../../context/SpaceProvider";
import { useMmText } from "../../hooks/useMmText";
import {
  getObjectContext,
  updateObject,
  type MmCapturedWithKind,
  type MmObjectContext,
  type MmRegister,
} from "../../api/mentalModels";
import { findTakeaway } from "../../api/userTakeaways";
import { sectionForTakeaway, stageWorkspaceItem, type WorkspaceItemSection } from "../../lib/workspaceHandoff";
import { AddChip, ChipSelect } from "../capture/ChipSelect";

const MAX_AREAS = 6;

const KIND_ICON: Record<MmCapturedWithKind, React.ElementType> = {
  insight: Lightbulb,
  action: CheckSquare,
  mental_model: Brain,
  project: FolderOpen,
  quality: Sparkles,
};

const clip = (text: string | undefined, n: number) => {
  const s = (text ?? "").trim();
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

export const mmContextKey = (objectId: string) => ["mm-context", objectId];

/** The model's context (areas, origin, captured-with, links) — one shared query. */
export const useMmContext = (objectId: string) => {
  const retryFetch = useRetryFetch();
  return useQuery({
    queryKey: mmContextKey(objectId),
    queryFn: () => getObjectContext(retryFetch, objectId),
    staleTime: 30_000,
  });
};

const norm = (s: string | undefined) => (s ?? "").replace(/\s+/g, " ").trim().toLowerCase();

interface AreaProps {
  objectId: string;
  register: MmRegister;
  /** The areas changed (lets the host list re-section without a refetch). */
  onSpacesChange?: (spaceIds: string[]) => void;
}

/**
 * The model's areas as inline, editable chips (spec 2026-11-03 model page §A1),
 * saved through the PATCH. Waits for the saved areas so an edit can't
 * overwrite them, and hides on error.
 */
export const MmAreaChips: React.FC<AreaProps> = ({ objectId, register, onSpacesChange }) => {
  const { tm } = useMmText(register);
  const retryFetch = useRetryFetch();
  const queryClient = useQueryClient();
  const { spaces } = useSpaces();
  const { data, isError } = useMmContext(objectId);

  const [areaIds, setAreaIds] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  useEffect(() => {
    setAreaIds(data ? (data.spaces ?? []).map((s) => s.id) : null);
  }, [data]);

  const spaceName = (id: string) =>
    spaces.find((s) => s._id === id)?.name ?? data?.spaces?.find((s) => s.id === id)?.name ?? "";

  const saveAreas = async (next: string[]) => {
    const prev = areaIds ?? [];
    setAreaIds(next);
    setSaving(true);
    setSaveFailed(false);
    try {
      await updateObject(retryFetch, objectId, { spaceIds: next });
      onSpacesChange?.(next);
      queryClient.setQueryData<MmObjectContext | undefined>(mmContextKey(objectId), (old) =>
        old ? { ...old, spaces: next.map((id) => ({ id, name: spaceName(id) })) } : old
      );
      queryClient.invalidateQueries({ queryKey: ["space-mental-models"] });
      queryClient.invalidateQueries({ queryKey: ["workspace", "focus", "models"] });
    } catch {
      setAreaIds(prev);
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  if (isError || areaIds === null) return null;
  const ids = areaIds;
  const free = spaces.map((s) => s._id).filter((id) => !ids.includes(id));

  return (
    <div>
      <div className="flex flex-wrap items-start gap-1.5">
        {ids.map((id) => (
          <ChipSelect
            key={id}
            value={id}
            options={[id, ...free]}
            label={spaceName}
            ariaLabel={tm("area", "Area")}
            onChange={(v) => v !== id && saveAreas(ids.map((x) => (x === id ? v : x)))}
            onRemove={() => saveAreas(ids.filter((x) => x !== id))}
            removeLabel={tm("area_remove", "Remove from this area")}
            disabled={saving}
            tone="primary"
          />
        ))}
        {free.length > 0 && ids.length < MAX_AREAS && (
          <AddChip
            text={ids.length === 0 ? tm("area_add_first", "Add to an area") : tm("area_add", "Area")}
            options={free}
            label={spaceName}
            ariaLabel={tm("area_add_first", "Add to an area")}
            onChange={(v) => saveAreas([...ids, v])}
            disabled={saving}
            highlight={ids.length === 0}
          />
        )}
        {ids.length === 0 && free.length === 0 && (
          <span className="text-xs text-muted-foreground">{tm("area_none", "Not in an area yet")}</span>
        )}
      </div>
      {saveFailed && (
        <p role="alert" className="text-xs text-destructive mt-1">
          {tm("area_save_failed", "Couldn't save that. Try again.")}
        </p>
      )}
    </div>
  );
};

interface ConnectedProps {
  objectId: string;
  register: MmRegister;
  /** The words the page already shows — the origin quote repeats only when it differs. */
  statements: Array<string | undefined>;
  /** Open another model in place (select it in the Models view). */
  onOpenModel: (id: string) => void;
}

/**
 * "Connected" (spec 2026-11-03 model page §A2): one strip of chips — what was
 * captured with it and the models it links to (with the link type). The
 * origin quote shows only when it differs from the model's words. Hidden when
 * there is nothing to show.
 */
export const MmConnected: React.FC<ConnectedProps> = ({ objectId, register, statements, onOpenModel }) => {
  const { tm } = useMmText(register);
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useMmContext(objectId);

  const toPortal = () => {
    if (location.pathname !== "/portal") navigate("/portal");
  };
  const openWorkspaceItem = (section: WorkspaceItemSection, id: string) => {
    stageWorkspaceItem(section, id);
    toPortal();
  };
  const openTakeaway = async (id: string) => {
    try {
      const res = await findTakeaway(retryFetch, id);
      openWorkspaceItem(sectionForTakeaway(res.item?.type), id);
    } catch {
      openWorkspaceItem("insights", id);
    }
  };
  const openSibling = (kind: MmCapturedWithKind, id: string) => {
    if (kind === "project") navigate(`/project/${id}`);
    else if (kind === "mental_model") onOpenModel(id);
    else if (kind === "insight") openWorkspaceItem("insights", id);
    else if (kind === "action") openWorkspaceItem("action_items", id);
  };

  if (!data) return null;
  const origin = data.origin;
  const capturedWith = data.capturedWith ?? [];
  const links = data.links ?? [];
  const quote =
    origin?.text && !statements.some((st) => norm(st) === norm(origin.text)) ? origin.text : null;
  if (capturedWith.length === 0 && links.length === 0 && !quote) return null;

  const chip =
    "inline-flex items-center gap-1.5 max-w-full rounded-full border px-2.5 py-1 min-h-[32px] text-xs transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";
  const neutral = "border-border bg-background text-foreground/80 hover:border-primary/30 hover:text-foreground";

  return (
    <section aria-label={tm("connected", "Connected")} className="space-y-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {tm("connected", "Connected")}
      </h3>
      {(capturedWith.length > 0 || links.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {capturedWith.map((c) => {
            const Icon = KIND_ICON[c.kind] ?? Brain;
            const text = clip(c.text, 60);
            const cls = cn(chip, c.kind === "mental_model" ? mmTypeStyle(undefined).chip : neutral);
            return c.kind === "quality" ? (
              <span key={`${c.kind}:${c.id}`} dir="auto" className={cls}>
                <Icon className="w-3 h-3 flex-none" />
                <span className="truncate">{text}</span>
              </span>
            ) : (
              <button
                key={`${c.kind}:${c.id}`}
                type="button"
                dir="auto"
                onClick={() => openSibling(c.kind, c.id)}
                className={cls}
              >
                <Icon className="w-3 h-3 flex-none" />
                <span className="truncate">{text}</span>
              </button>
            );
          })}
          {links.map((l) => {
            const style = mmTypeStyle(l.objectType);
            const Icon = style.icon;
            return (
              <button
                key={`${l.incoming ? "in" : "out"}:${l.id}:${l.linkType}`}
                type="button"
                onClick={() => onOpenModel(l.id)}
                className={cn(chip, style.chip)}
              >
                <Icon className="w-3 h-3 flex-none" />
                <span className="inline-flex items-center gap-0.5 flex-none opacity-75">
                  {l.incoming ? (
                    <ArrowLeft className="w-3 h-3 rtl:rotate-180" />
                  ) : (
                    <ArrowRight className="w-3 h-3 rtl:rotate-180" />
                  )}
                  {tm(`link_type_${l.linkType}`, l.linkType.replace(/_/g, " "))}
                </span>
                <span dir="auto" className="truncate">
                  {clip(l.label, 60)}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {quote &&
        (origin?.kind === "user_takeaway" ? (
          <button
            type="button"
            dir="auto"
            onClick={() => openTakeaway(origin.id)}
            className="block text-start text-xs italic text-muted-foreground hover:text-primary"
          >
            {tm("from", "From")}: “{clip(quote, 200)}”
          </button>
        ) : (
          <p dir="auto" className="text-xs italic text-muted-foreground">
            {tm("from", "From")}: “{clip(quote, 200)}”
          </p>
        ))}
    </section>
  );
};
